import { ruRU, enUS, deDE, frFR, esES } from "@mui/material/locale";
import { createTheme, ThemeOptions } from "@mui/material/styles";
import { designTokens } from "./theme/design-tokens";

const MUI_LOCALE_MAP: Record<string, object> = {
  ru: ruRU,
  en: enUS,
  de: deDE,
  fr: frFR,
  es: esES,
};

// Создаем расширенную тему для Material UI с новыми design tokens
const createEventMasterTheme = (mode: "light" | "dark" = "light", language = "ru") => {
  const muiLocale = MUI_LOCALE_MAP[language] ?? enUS;
  const tokens = designTokens;

  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: tokens.colors.primary[500],
        light: tokens.colors.primary[300],
        dark: tokens.colors.primary[700],
        contrastText: tokens.colors.text.inverse,
      },
      secondary: {
        main: tokens.colors.secondary[500],
        light: tokens.colors.secondary[300],
        dark: tokens.colors.secondary[700],
        contrastText: tokens.colors.text.inverse,
      },
      error: {
        main: tokens.colors.error[500],
        light: tokens.colors.error[300],
        dark: tokens.colors.error[700],
        contrastText: tokens.colors.text.inverse,
      },
      warning: {
        main: tokens.colors.warning[500],
        light: tokens.colors.warning[300],
        dark: tokens.colors.warning[700],
        contrastText: tokens.colors.text.primary,
      },
      info: {
        main: tokens.colors.info[500],
        light: tokens.colors.info[300],
        dark: tokens.colors.info[700],
        contrastText: tokens.colors.text.inverse,
      },
      success: {
        main: tokens.colors.success[500],
        light: tokens.colors.success[300],
        dark: tokens.colors.success[700],
        contrastText: tokens.colors.text.inverse,
      },
      background: {
        default:
          mode === "light"
            ? tokens.colors.background.default
            : tokens.colors.gray[900],
        paper:
          mode === "light"
            ? tokens.colors.background.paper
            : tokens.colors.gray[800],
      },
      text: {
        primary:
          mode === "light"
            ? tokens.colors.text.primary
            : tokens.colors.text.inverse,
        secondary:
          mode === "light"
            ? tokens.colors.text.secondary
            : "rgba(255, 255, 255, 0.7)",
        disabled:
          mode === "light"
            ? tokens.colors.text.disabled
            : "rgba(255, 255, 255, 0.38)",
      },
      divider:
        mode === "light"
          ? tokens.colors.border.light
          : "rgba(255, 255, 255, 0.12)",
      grey: tokens.colors.gray,
    },

    typography: {
      fontFamily: tokens.typography.fontFamily.primary.join(","),

      // Заголовки
      h1: {
        fontSize: tokens.typography.fontSize["5xl"],
        fontWeight: tokens.typography.fontWeight.bold,
        lineHeight: tokens.typography.lineHeight.tight,
        letterSpacing: tokens.typography.letterSpacing.tight,
      },
      h2: {
        fontSize: tokens.typography.fontSize["4xl"],
        fontWeight: tokens.typography.fontWeight.bold,
        lineHeight: tokens.typography.lineHeight.tight,
        letterSpacing: tokens.typography.letterSpacing.tight,
      },
      h3: {
        fontSize: tokens.typography.fontSize["3xl"],
        fontWeight: tokens.typography.fontWeight.semibold,
        lineHeight: tokens.typography.lineHeight.tight,
      },
      h4: {
        fontSize: tokens.typography.fontSize["2xl"],
        fontWeight: tokens.typography.fontWeight.semibold,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      h5: {
        fontSize: tokens.typography.fontSize.xl,
        fontWeight: tokens.typography.fontWeight.medium,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      h6: {
        fontSize: tokens.typography.fontSize.lg,
        fontWeight: tokens.typography.fontWeight.medium,
        lineHeight: tokens.typography.lineHeight.normal,
      },

      // Основной текст
      body1: {
        fontSize: tokens.typography.fontSize.base,
        fontWeight: tokens.typography.fontWeight.normal,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      body2: {
        fontSize: tokens.typography.fontSize.sm,
        fontWeight: tokens.typography.fontWeight.normal,
        lineHeight: tokens.typography.lineHeight.normal,
      },

      // Подписи и мелкий текст
      caption: {
        fontSize: tokens.typography.fontSize.xs,
        fontWeight: tokens.typography.fontWeight.normal,
        lineHeight: tokens.typography.lineHeight.normal,
        color: tokens.colors.text.secondary,
      },

      // Кнопки
      button: {
        fontSize: tokens.typography.fontSize.sm,
        fontWeight: tokens.typography.fontWeight.medium,
        textTransform: "none",
        letterSpacing: tokens.typography.letterSpacing.wide,
      },
    },

    spacing: 8, // базовая единица spacing (8px)

    shape: {
      borderRadius: parseInt(tokens.borderRadius.base.replace("rem", "")) * 16, // Convert rem to px
    },

    shadows: [
      "none",
      tokens.shadows.sm,
      tokens.shadows.base,
      tokens.shadows.md,
      tokens.shadows.md,
      tokens.shadows.lg,
      tokens.shadows.lg,
      tokens.shadows.xl,
      tokens.shadows.xl,
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
      tokens.shadows["2xl"],
    ],

    breakpoints: {
      values: {
        xs: parseInt(tokens.breakpoints.xs),
        sm: parseInt(tokens.breakpoints.sm),
        md: parseInt(tokens.breakpoints.md),
        lg: parseInt(tokens.breakpoints.lg),
        xl: parseInt(tokens.breakpoints.xl),
      },
    },

    transitions: {
      duration: {
        shortest: parseInt(tokens.transitions.duration.fastest),
        shorter: parseInt(tokens.transitions.duration.fast),
        short: parseInt(tokens.transitions.duration.normal),
        standard: parseInt(tokens.transitions.duration.normal),
        complex: parseInt(tokens.transitions.duration.slow),
        enteringScreen: parseInt(tokens.transitions.duration.normal),
        leavingScreen: parseInt(tokens.transitions.duration.fast),
      },
      easing: {
        easeInOut: tokens.transitions.easing.easeInOut,
        easeOut: tokens.transitions.easing.easeOut,
        easeIn: tokens.transitions.easing.easeIn,
        sharp: tokens.transitions.easing.linear,
      },
    },

    components: {
      // Button компонент
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: tokens.borderRadius.base,
            fontWeight: tokens.typography.fontWeight.medium,
            transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.easing.easeInOut}`,
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: tokens.shadows.md,
            },
          },
          sizeSmall: {
            height: tokens.components.button.height.sm,
            padding: tokens.components.button.padding.sm,
            fontSize: tokens.typography.fontSize.xs,
            borderRadius: tokens.components.button.borderRadius.sm,
          },
          sizeMedium: {
            height: tokens.components.button.height.md,
            padding: tokens.components.button.padding.md,
            fontSize: tokens.typography.fontSize.sm,
            borderRadius: tokens.components.button.borderRadius.md,
          },
          sizeLarge: {
            height: tokens.components.button.height.lg,
            padding: tokens.components.button.padding.lg,
            fontSize: tokens.typography.fontSize.base,
            borderRadius: tokens.components.button.borderRadius.lg,
          },
        },
      },

      // Paper компонент
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.base,
            backgroundImage: "none",
          },
          elevation1: {
            boxShadow: tokens.shadows.sm,
          },
          elevation2: {
            boxShadow: tokens.shadows.base,
          },
          elevation3: {
            boxShadow: tokens.shadows.md,
          },
          elevation4: {
            boxShadow: tokens.shadows.lg,
          },
        },
      },

      // Card компонент
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: tokens.components.card.borderRadius,
            boxShadow: tokens.shadows.base,
            border: `1px solid ${tokens.colors.border.light}`,
            transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.easing.easeInOut}`,
            "&:hover": {
              boxShadow: tokens.shadows.lg,
              transform: "translateY(-2px)",
            },
          },
        },
      },

      // TextField компонент
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: tokens.components.input.borderRadius,
              "& fieldset": {
                borderColor: tokens.colors.border.light,
              },
              "&:hover fieldset": {
                borderColor: tokens.colors.border.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: tokens.colors.border.focus,
                borderWidth: "2px",
              },
            },
          },
        },
      },

      // Chip компонент
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.full,
            fontWeight: tokens.typography.fontWeight.medium,
          },
        },
      },

      // AppBar компонент
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: tokens.shadows.sm,
            borderBottom: `1px solid ${tokens.colors.border.light}`,
          },
        },
      },

      // Drawer компонент
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${tokens.colors.border.light}`,
            boxShadow: "none",
          },
        },
      },

      // Dialog компонент
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: tokens.components.modal.borderRadius,
            boxShadow: tokens.shadows.xl,
          },
        },
      },

      // Tooltip компонент
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: tokens.colors.gray[800],
            fontSize: tokens.typography.fontSize.xs,
            borderRadius: tokens.borderRadius.base,
            padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
          },
        },
      },
    },
  };

  return createTheme(themeOptions, muiLocale);
};

// Экспортируем светлую тему по умолчанию
const theme = createEventMasterTheme("light");

// Экспортируем также функцию для создания темы
export { createEventMasterTheme };
export default theme;
